import { createContext, useEffect, useState } from 'react'

export type SiteConfig = {
  isLoading: boolean
  error: boolean
  stripePublishableKey?: string
  price?: string
  kindeClientId?: string
  kindeDomain?: string
  kindeAudience?: string
  /**
   * The runtime PostHog key/host, following the exact pattern every other
   * field here already established — never a build-time constant. Both are
   * optional and absent-tolerant: a /config.json with no POSTHOG_KEY (e.g.
   * before infra publishes one) leaves this undefined and analytics stays
   * inert (see src/analytics/createAnalyticsClient.ts).
   */
  posthogKey?: string
  posthogHost?: string
}

export const SettingsContext = createContext<SiteConfig>({
  isLoading: true,
  error: false,
})

export function useSiteConfig() {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [stripePublishableKey, setStripePublishableKey] = useState<string>()
  const [price, setPrice] = useState<string>()
  const [kindeClientId, setKindeClientId] = useState<string>()
  const [kindeDomain, setKindeDomain] = useState<string>()
  const [kindeAudience, setKindeAudience] = useState<string>()
  const [posthogKey, setPosthogKey] = useState<string>()
  const [posthogHost, setPosthogHost] = useState<string>()

  useEffect(() => {
    const doEffect = async () => {
      try {
        const response = await fetch('/config.json')
        if (!response.ok) {
          setError(true)
          setIsLoading(false)
          throw new Error(`Response status: ${response.status}`)
        }

        const json = await response.json()
        setStripePublishableKey(json.STRIPE_PUBLISHABLE_KEY)
        setPrice(json.PRICE)
        setKindeClientId(json.KINDE_CLIENT_ID)
        setKindeDomain(json.KINDE_DOMAIN)
        setKindeAudience(json.KINDE_AUDIENCE)
        setPosthogKey(json.POSTHOG_KEY)
        setPosthogHost(json.POSTHOG_HOST)
        setIsLoading(false)
      } catch (err) {
        console.error('Failed to load site config:', err)
        setError(true)
        setIsLoading(false)
      }
    }
    doEffect()
  }, [])

  return {
    isLoading,
    error,
    stripePublishableKey,
    price,
    kindeClientId,
    kindeDomain,
    kindeAudience,
    posthogKey,
    posthogHost,
  }
}
